import {
  Ingredient,
  IngredientRecipeStep,
  RoundType,
} from '../types/fam-graphql'

export const onlyOneDecimal = (num: number) =>
  Math.round((num + Number.EPSILON) * 10) / 10

export const onlyTwoDecimals = (num: number) =>
  Math.round((num + Number.EPSILON) * 100) / 100

const GCD = (a: number, b: number): number => {
  if (!b) return a
  return GCD(b, a % b)
}

const numberToFraction = (number: number): string => {
  const [integer, decimal] = number.toString().split('.')
  if (!decimal) return integer

  const denominator = 10 ** decimal.length
  const numerator = parseInt(decimal, 10)
  const gcd = GCD(numerator, denominator)

  if (denominator / gcd > 4) {
    const allowedFractions = [1 / 4, 1 / 3, 1 / 2, 2 / 3, 3 / 4]
    const literalFractions = ['1/4', '1/3', '1/2', '2/3', '3/4']
    let abs = 1
    let index = 0
    allowedFractions?.forEach((v, i) => {
      const localAbs = Math.abs(number - v)
      if (localAbs < abs) {
        abs = localAbs
        index = i
      }
    })

    return literalFractions[index]
  }

  return `${numerator / gcd}/${denominator / gcd}`
}

const UNIT_NO_FRACTION_EXCEPTIONS = [
  'g',
  'cl',
  'ml',
  'dl',
  'l',
  'kg',
  'kg (avec coquille)',
  'cm',
]
const UNIT_NOT_BELOW_1_EXCEPTIONS = [
  'pincée',
  'filet',
  'brin',
  'goutte',
  'noix',
  'noisette',
  'grain',
  'pétale',
  'pilon',
  'cannelloni',
  'pointe',
]

/**
Rules:
- ROUND_1 => Round to the nearest 0.5, display 1 decimal place max. If less than 1, display as a fraction.
- ROUND_2 => Round to the nearest 0.01, display 2 decimal places max.
- ROUND_3 => Round to the nearest integer.

Exceptions:
- If unit is in UNIT_NO_FRACTION_EXCEPTIONS, don't display as a fraction.
- If unit is in UNIT_NOT_BELOW_1_EXCEPTIONS, don't display as a fraction and don't display less than 1.
**/
export const applyRoundRule = (
  number: number,
  rule: RoundType,
  unitName: string
): number | string => {
  let hasFractionException = false
  if (UNIT_NO_FRACTION_EXCEPTIONS.includes(unitName)) {
    hasFractionException = true
  }

  let hasNotBelow1Exception = false
  if (UNIT_NOT_BELOW_1_EXCEPTIONS.includes(unitName)) {
    hasNotBelow1Exception = true
  }

  if (number < 1 && hasNotBelow1Exception) {
    return 1
  }

  switch (rule) {
    case RoundType.Round_1: {
      const rounded = Math.round(number * 2) / 2
      if (number < 1 && !hasFractionException) {
        return numberToFraction(number)
      }
      return onlyOneDecimal(rounded)
    }
    case RoundType.Round_2:
      return onlyTwoDecimals(number)
    case RoundType.Round_3:
      return Math.round(number)
    default:
      return number
  }
}

export const unitsTransformer = (ingredient: any, quantity: number) => {
  const transformQuantity = applyRoundRule(
    quantity,
    ingredient?.measurement_unit?.round_type,
    ingredient.measurement_unit?.name ?? ''
  )

  if (ingredient.measurement_unit?.use_ingredient_name) {
    return transformQuantity ?? quantity
  }

  if (ingredient.measurement_unit?.name === 'g') {
    return null
  }

  return `${transformQuantity} ${
    transformQuantity >= 2
      ? ingredient.measurement_unit?.plural ?? ingredient.measurement_unit?.name
      : ingredient.measurement_unit?.name ?? ''
  }`
}

export const weightTransformer = (
  ingredient: Ingredient,
  multiplicationFactor: number
) => {
  if (!ingredient?.ingredient_recipe?.gross_weight) {
    return null
  }

  const weight = applyRoundRule(
    ingredient?.ingredient_recipe?.gross_weight * multiplicationFactor,
    ingredient?.ingredient_recipe?.measurement_unit?.round_type ??
      RoundType.Round_1,
    ingredient?.ingredient_recipe?.measurement_unit?.name ?? ''
  )

  return `${weight} g`
}

export const weightStepTransformer = (
  ingredient: IngredientRecipeStep | null
) => {
  if (!ingredient?.gross_weight) {
    return null
  }

  return `${ingredient?.gross_weight} g`
}

export const dayQueryTransformer = (day: string) => day.replace('jour', 'DAY')
